import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Plus, Check, Eye } from "lucide-react";
import { formatPrice } from "@/lib/format";

export const ProductCard = ({ product, onQuickView, onAdd }) => {
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    onAdd(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group"
    >
      <div className="relative overflow-hidden rounded-2xl bg-[#F4F0EA] dark:bg-[#1C1917]">
        <div className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
          style={{ background: "radial-gradient(circle at 50% 35%, rgba(251,191,36,0.22), transparent 65%)" }}
        />
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 z-20 rounded-full bg-white/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-700 backdrop-blur-sm dark:bg-stone-900/85 dark:text-stone-200">
          {product.badge}
        </span>
        {product.colors?.length > 1 && (
          <div
            data-testid={`product-colors-hint-${product.id}`}
            className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 rounded-full bg-white/90 py-1.5 pl-1.5 pr-3 shadow-md backdrop-blur-sm dark:bg-stone-900/90"
            title={`Disponible en ${product.colors.length} acabados`}
          >
            {product.colors.slice(0, 3).map((c) => (
              <img
                key={c.name}
                src={c.image}
                alt={c.name}
                className="h-5 w-5 rounded-full border border-stone-200 object-cover dark:border-stone-700"
              />
            ))}
            <span className="font-mono text-[10px] font-medium text-stone-700 dark:text-stone-200">
              {product.colors.length} colores
            </span>
          </div>
        )}
        <button
          data-testid={`product-quick-view-button-${product.id}`}
          onClick={() => onQuickView(product)}
          className="absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-white/90 px-5 py-2.5 text-xs font-medium text-stone-900 opacity-0 shadow-lg backdrop-blur-sm transition-all duration-300 hover:bg-white group-hover:opacity-100 dark:bg-stone-900/90 dark:text-stone-100"
        >
          <Eye size={14} /> Vista rápida
        </button>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl tracking-snug text-stone-900 dark:text-stone-50 sm:text-2xl">
            {product.name}
          </h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 dark:text-stone-400">
            {product.material}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <span className="flex items-center gap-1 text-xs text-stone-600 dark:text-stone-300">
              <Star size={12} className="fill-amber-500 text-amber-500" />
              {product.rating}
            </span>
            <span className="text-xs text-stone-400">({product.reviews_count})</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <p className="text-base font-semibold text-stone-900 dark:text-stone-50">{formatPrice(product.price)}</p>
            {product.original_price > product.price && (
              <p className="text-xs text-stone-400 line-through">{formatPrice(product.original_price)}</p>
            )}
          </div>
          <button
            data-testid={`product-add-to-cart-button-${product.id}`}
            onClick={handleAdd}
            aria-label={`Añadir ${product.name} al carrito`}
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
              added
                ? "bg-amber-600 text-white"
                : "bg-stone-900 text-[#FDFBF7] hover:scale-110 hover:bg-amber-700 dark:bg-stone-50 dark:text-stone-900 dark:hover:bg-amber-400"
            }`}
          >
            {added ? <Check size={16} /> : <Plus size={16} />}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
